/**
 * LiveTicker — horizontal marquee of recent matches and activity.
 * Designed in the spirit of Bloomberg/Polymarket tickers: dense, quiet,
 * mono font for numbers, brand color used only on win amounts.
 *
 * Data is currently mocked (plausible Solana addresses, varied games)
 * but structured so it can be swapped for a websocket feed later.
 */

interface TickerEvent {
  kind: "win" | "match" | "stat";
  text: React.ReactNode;
}

const SHORT_ADDR = (full: string) =>
  `${full.slice(0, 6)}…${full.slice(-4)}`;

// Mock recent activity (would come from chain events in production)
const EVENTS: TickerEvent[] = [
  { kind: "win",   text: <>{SHORT_ADDR("4fWzK89ddE7e2aabPae8a2mVKpTs3RbNcJhGdU5yqA")} won <Amt v={20.00} /> on <G>PONG</G></> },
  { kind: "match", text: <>{SHORT_ADDR("Ca39f742bb82F3fdWceJ3e5mHkPqRvNsT8uYxZ4wDe")} entered <G>FPS Arena</G> · stake <Amt v={50} /></> },
  { kind: "win",   text: <>{SHORT_ADDR("9d7c33f48adK8dac42ef7cbcQmRsTuVwXyZ2E3F4Gh")} won <Amt v={189.50} /> on <G>AWP duel</G></> },
  { kind: "stat",  text: <><Live /> 12 matches active right now</> },
  { kind: "win",   text: <>{SHORT_ADDR("2ab78124cefa9W51d3b9eK82aMnPqRsTuVwXyZ5E6J")} won <Amt v={42.00} /> on <G>PONG</G></> },
  { kind: "match", text: <>{SHORT_ADDR("Fe841629d99c47fa67f1JS9ceKmNpQrStUvWxYz7Eb")} matched in <Latency ms={1.2} /></> },
  { kind: "win",   text: <>{SHORT_ADDR("71c995a7d3f12cb4A9a8e221f4dKmNpQrStUvWxYzC")} won <Amt v={9.00} /> on <G>FPS Arena</G></> },
  { kind: "stat",  text: <>Avg payout last hour <Amt v={36.80} /></> },
  { kind: "win",   text: <>{SHORT_ADDR("d83aB2fef1c6e7e5d82f88a4fKmNpQrStUvWxYz8Ed")} won <Amt v={76.40} /> on <G>PONG</G></> },
  { kind: "match", text: <>New match queued · <G>FPS Arena</G> · stake <Amt v={100} /></> },
  { kind: "win",   text: <>{SHORT_ADDR("B4e1c7a9a44fV2cce2c8d7f23KmNpQrStUvWxYz9E")} won <Amt v={18.00} /> on <G>PONG</G></> },
  { kind: "stat",  text: <><Live /> 24h volume <Amt v={142_310} compact /></> },
];

function Amt({ v, compact = false }: { v: number; compact?: boolean }) {
  const display = compact && v >= 1000
    ? `$${(v / 1000).toFixed(1)}K`
    : `$${v.toFixed(2)}`;
  return (
    <span className="font-mono tabular-nums text-[var(--color-brand)] font-medium">
      {display}
    </span>
  );
}

function G({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[var(--color-text-primary)] font-medium">
      {children}
    </span>
  );
}

function Latency({ ms }: { ms: number }) {
  return (
    <span className="font-mono tabular-nums text-[var(--color-live)]">{ms.toFixed(1)}s</span>
  );
}

function Live() {
  return (
    <span className="inline-flex items-center gap-1.5 mr-0.5">
      <span className="relative flex h-1 w-1">
        <span className="animate-pulse-dot absolute inline-flex h-full w-full rounded-full bg-[var(--color-live)]" />
        <span className="relative inline-flex rounded-full h-1 w-1 bg-[var(--color-live)]" />
      </span>
    </span>
  );
}

function Sep() {
  return (
    <span className="mx-6 text-[var(--color-text-disabled)] select-none" aria-hidden>
      ◆
    </span>
  );
}

function TickerRow({ events }: { events: TickerEvent[] }) {
  return (
    <div className="flex items-center whitespace-nowrap shrink-0">
      {events.map((e, i) => (
        <span key={i} className="inline-flex items-center text-[13px] text-[var(--color-text-secondary)]">
          {e.text}
          {i < events.length - 1 && <Sep />}
        </span>
      ))}
      <Sep />
    </div>
  );
}

export default function LiveTicker() {
  return (
    <div className="relative hairline-t hairline-b bg-[var(--color-canvas)] overflow-hidden">
      {/* Edge fade masks */}
      <div
        className="absolute inset-y-0 left-0 w-24 z-10 pointer-events-none"
        style={{ background: "linear-gradient(to right, var(--color-canvas), transparent)" }}
      />
      <div
        className="absolute inset-y-0 right-0 w-24 z-10 pointer-events-none"
        style={{ background: "linear-gradient(to left, var(--color-canvas), transparent)" }}
      />

      {/* Left label badge */}
      <div className="absolute left-0 top-0 bottom-0 z-20 flex items-center gap-2 pl-4 pr-5 hairline-r bg-[var(--color-canvas)]">
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-pulse-dot absolute inline-flex h-full w-full rounded-full bg-[var(--color-live)]" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[var(--color-live)]" />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-text-tertiary)]">
          Live Feed
        </span>
      </div>

      {/* Marquee track — duplicated for seamless loop */}
      <div className="flex pl-32 py-3 animate-marquee will-change-transform">
        <TickerRow events={EVENTS} />
        <TickerRow events={EVENTS} />
      </div>
    </div>
  );
}
